import { z } from "zod";
import { BookingAttachment, ImageAttachment } from "../attachment.firestore";
import { CatalogItemId } from "../catalogItem";
import { CurrencyCode, Price } from "../currency";
import { OfferingCategory } from "../offeringCategory";
import {
    AgentId,
    FirebaseId,
    GeoPoint,
    MediumLongString,
    MemberId,
    PercentCrop,
    PixelCrop,
    Rating,
    UrlString,
} from "../primitives";
import { FirebaseTimestamp } from "../record.firestore";
import { BookingOption } from "./bookingOption";
import { BookingReservationStatus } from "./bookingReservationStatus";

// TODO: Copied (partially)from firebase/lmt/src/common/types. Replace the file there with imports from this file and move tests if any.

const BookingRequired = z.object({
    catalogItem: CatalogItemId.optional().nullable(),
    createdBy: AgentId,
    member: MemberId,
    request: FirebaseId,

    title: z.string().nullable(),
    archived: z.boolean(),
});

export const BookingMeta = z.object({
    createdAt: FirebaseTimestamp,
    updatedAt: FirebaseTimestamp.nullable().optional(),
    updatedBy: AgentId.optional().nullable(),

    prebookedAt: FirebaseTimestamp.optional().nullable(),
    confirmedAt: FirebaseTimestamp.optional().nullable(),
    cancelledAt: FirebaseTimestamp.optional().nullable(),
    calendarEventId: z.string().nullable(),
});
export type BookingMeta = z.infer<typeof BookingMeta>;

export const BookingUpdatables = z.object({
    title: z.string().optional().nullable(),
    description: z.string().optional().nullable(),
    country: z.string().optional().nullable(),
    website: UrlString.optional().nullable(),
    phoneNumber: z.string().optional().nullable(),
    address: z.string().optional().nullable(),
    offeringCategory: OfferingCategory.optional().nullable(),
    googleUrl: z.string().optional().nullable(),
    geoPoint: GeoPoint.optional().nullable(),
    numberOfGuests: z.number().optional().nullable(),
    numberOfNights: z.number().optional().nullable(),
    rooms: z.string().optional().nullable(),
    price: Price.optional().nullable(),
    // TODO: Drop `priceCurrency` from the database and from FE/BE business logic;
    // `price.currency` should be the only currency.
    priceCurrency: CurrencyCode.optional().nullable(),
    priceQuote: z.enum(["final", "estimate"]).optional().nullable(),
    paymentTo: z.enum(["albertine", "supplier"]).optional().nullable(),
    priceDetails: z.string().optional().nullable(),

    attachments: z.array(BookingAttachment).optional(),

    imageURL: z.string().optional().nullable(),
    absoluteCrop: PixelCrop.optional().nullable(),
    relativeCrop: PercentCrop.optional().nullable(),
    croppedImageURL: z.string().optional().nullable(),
    images: z.array(ImageAttachment).optional().nullable(),

    reference: z.string().optional().nullable(),
    reservationStatus: BookingReservationStatus.optional().nullable(),
    notesForMember: z.string().optional().nullable(),
    notesForMemberSenderId: AgentId.optional().nullable(),
    notesForMemberSenderName: z.string().optional().nullable(),

    suppliers: z.array(z.string()).optional().nullable(),
    supplierDetails: z.string().optional().nullable(),

    startTime: FirebaseTimestamp.optional().nullable(),
    startTimezone: z.string().optional().nullable(), // TODO: Create common type
    endTime: FirebaseTimestamp.optional().nullable(),
    endTimezone: z.string().optional().nullable(), // TODO: Create common type
    archived: z.boolean().optional(),
    reviewNeeded: z.boolean().optional().nullable(),

    headline: z.string().optional().nullable(),
    benefits: z.string().optional().nullable(),
    bookingPolicy: z.string().optional().nullable(),

    options: z.array(BookingOption).optional().nullable(),
});
export type BookingUpdatables = z.infer<typeof BookingUpdatables>;

export const BookingFeedback = z.object({
    bookingRating: Rating,
    bookingFeedback: MediumLongString.optional().nullable(),
    bookingFeedbackTimestamp: FirebaseTimestamp.optional().nullable(),
});
export type BookingFeedback = z.infer<typeof BookingFeedback>;

export const ProposalFeedbackEnum = z.enum(["like", "dislike"]);

export type ProposalFeedbackEnum = z.infer<typeof ProposalFeedbackEnum>;

export const ProposalFeedback = z.object({
    proposalFeedback: ProposalFeedbackEnum.optional().nullable(),
    proposalFeedbackTimestamp: FirebaseTimestamp.optional().nullable(),
});
export type ProposalFeedback = z.infer<typeof ProposalFeedback>;

const BookingId = z.object({ id: FirebaseId });

export const BookingDoc = BookingRequired.extend(BookingMeta.shape)
    .extend(BookingUpdatables.shape)
    .extend(ProposalFeedback.shape)
    .extend(BookingFeedback.shape);
export type BookingDoc = z.infer<typeof BookingDoc>;

export const MemberBookingDoc = BookingDoc.pick({
    title: true,
    description: true,
    offeringCategory: true,
    imageURL: true,
    croppedImageURL: true,
    images: true,
    attachments: true,
    startTime: true,
    startTimezone: true,
    endTime: true,
    endTimezone: true,
    numberOfGuests: true,
    numberOfNights: true,
    website: true,
    address: true,
    phoneNumber: true,
    geoPoint: true,
    googleUrl: true,
    notesForMember: true,
    notesForMemberSenderId: true,
    notesForMemberSenderName: true,
    rooms: true,
    benefits: true,
    price: true,
    priceDetails: true,
    reference: true,
    request: true,
    bookingFeedbackTimestamp: true,
    bookingRating: true,
    bookingFeedback: true,
    headline: true,
    bookingPolicy: true,
    proposalFeedback: true,
    reservationStatus: true,
    options: true,
});
export type MemberBookingDoc = z.infer<typeof MemberBookingDoc>;

export const Booking = z.object({ id: FirebaseId }).extend(BookingDoc.shape);
export type Booking = z.infer<typeof Booking>;

export const BookingInMessage = BookingId.extend(
    BookingRequired.pick({
        request: true,
        title: true,
    }).shape,
).extend(
    BookingUpdatables.omit({
        supplierDetails: true,
    }).shape,
);
export type BookingInMessage = z.infer<typeof BookingInMessage>;

export const BookingToConfirmInMemberMessage = BookingId.extend(
    BookingRequired.pick({
        title: true,
    }).shape,
).extend(
    BookingUpdatables.omit({
        supplierDetails: true,
    }).shape,
);
export type BookingToConfirmInMemberMessage = z.infer<
    typeof BookingToConfirmInMemberMessage
>;
